import {
    Activity,
    BarChart3,
    Blocks,
    Braces,
    CreditCard,
    FileText,
    Globe,
    House,
    Inbox,
    Languages,
    MapPin,
    Megaphone,
    MessageSquare,
    PackageCheck,
    Receipt,
    Settings,
    Shield,
    ShieldAlert,
    Store,
    Tag,
    Tags,
    Truck,
    UserRound,
    UsersRound,
} from 'lucide-react'

export type SidebarItemType = 'link' | 'group' | 'header' | 'plugins'

export interface SidebarItem {
    type: SidebarItemType
    label?: string
    to?: string
    icon?: any
    /** Hidden unless every listed flag is enabled (a dot path, e.g. `orders.all_orders`). */
    featureFlag?: string | string[]
    id?: string // For collapsible state key
    /** Rendered in the pinned block at the bottom of the sidebar (Settings). */
    placement?: 'bottom'
    /** Settings nav only: shown indented under the sibling with this `to` (e.g. delivery methods under shipping). */
    parentTo?: string
    children?: SidebarItem[]
}

/*
 * Admin information architecture, grouped under short section labels (Sell, Grow, Store, Apps). A group's first
 * visible child is its landing page: the parent row links there and the remaining children show as sub-items while
 * the group is active. Routes and feature flags are unchanged (docs/ADMIN_SHOPIFY_REDESIGN_PLAN.md §4).
 */
export const sidebarConfig: SidebarItem[] = [
    {
        type: 'link',
        to: '/',
        label: 'Home',
        icon: House,
        featureFlag: 'dashboard',
    },
    { type: 'header', label: 'Sell' },
    {
        type: 'group',
        id: 'orders',
        label: 'Orders',
        icon: Inbox,
        children: [
            { type: 'link', to: '/orders', label: 'Orders', featureFlag: ['orders', 'orders.all_orders'] },
            { type: 'link', to: '/orders/sources', label: 'Order sources', featureFlag: ['orders', 'orders.source_list'] },
            { type: 'link', to: '/orders/notes', label: 'Order notes', featureFlag: ['orders', 'orders.note_list'] },
            { type: 'link', to: '/returns', label: 'Returns', featureFlag: 'returns' },
        ],
    },
    {
        type: 'group',
        id: 'products',
        label: 'Products',
        icon: Tag,
        children: [
            { type: 'link', to: '/products', label: 'Products', featureFlag: ['products', 'products.all_products'] },
            { type: 'link', to: '/categories', label: 'Categories', featureFlag: ['products', 'products.categories'] },
            { type: 'link', to: '/brands', label: 'Brands', featureFlag: ['products', 'products.brands'] },
            { type: 'link', to: '/attributes', label: 'Attributes', featureFlag: ['products', 'products.attributes'] },
            { type: 'link', to: '/inventory', label: 'Inventory', featureFlag: 'inventory' },
            { type: 'link', to: '/reviews', label: 'Reviews', featureFlag: 'reviews' },
        ],
    },
    {
        type: 'link',
        to: '/customers',
        label: 'Customers',
        icon: UserRound,
        featureFlag: 'customers',
    },
    { type: 'header', label: 'Grow' },
    {
        type: 'group',
        id: 'marketing',
        label: 'Marketing',
        icon: Megaphone,
        children: [
            { type: 'link', to: '/campaigns', label: 'Campaigns', featureFlag: 'campaigns' },
            { type: 'link', to: '/marketing', label: 'Email marketing', featureFlag: 'marketing' },
        ],
    },
    {
        type: 'link',
        to: '/coupons',
        label: 'Coupons',
        icon: Tags,
        featureFlag: 'coupons',
    },
    {
        type: 'group',
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        children: [
            { type: 'link', to: '/analytics', label: 'Analytics' },
            { type: 'link', to: '/reports/sales', label: 'Sales report' },
            { type: 'link', to: '/reports/revenue', label: 'Revenue report' },
            { type: 'link', to: '/reports/customers', label: 'Customer report' },
            { type: 'link', to: '/reports/inventory', label: 'Inventory report' },
        ],
    },
    { type: 'header', label: 'Store' },
    {
        type: 'link',
        to: '/themes',
        label: 'Online store',
        icon: Store,
        featureFlag: 'themes',
    },
    {
        type: 'group',
        id: 'content',
        label: 'Content',
        icon: FileText,
        children: [
            { type: 'link', to: '/blog', label: 'Blog posts', featureFlag: 'blog' },
            { type: 'link', to: '/media', label: 'Files', featureFlag: 'media.media_library' },
        ],
    },
    { type: 'header', label: 'Apps' },
    {
        type: 'link',
        to: '/plugins',
        label: 'Plugins',
        icon: Blocks,
        featureFlag: 'plugins',
    },
    // Installed plugins' nav items are inserted here.
    {
        type: 'plugins',
    },
    {
        type: 'group',
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        placement: 'bottom',
        children: [
            { type: 'link', to: '/settings', label: 'General', icon: Store, featureFlag: 'settings' },
            { type: 'link', to: '/shipping', label: 'Shipping and delivery', icon: Truck, featureFlag: 'shipping' },
            { type: 'link', to: '/orders/methods', label: 'Delivery methods', icon: PackageCheck, featureFlag: 'orders.delivery_methods', parentTo: '/shipping' },
            { type: 'link', to: '/orders/locations', label: 'Delivery areas', icon: MapPin, featureFlag: ['orders', 'locations'], parentTo: '/shipping' },
            { type: 'link', to: '/taxes', label: 'Taxes', icon: Receipt, featureFlag: 'taxes' },
            { type: 'link', to: '/system/payment-gateways', label: 'Payments', icon: CreditCard, featureFlag: 'system.payment_gateways' },
            { type: 'link', to: '/system/sms-gateways', label: 'SMS', icon: MessageSquare, featureFlag: 'system.sms_gateways' },
            { type: 'link', to: '/system/administration', label: 'Users', icon: UsersRound, featureFlag: 'administration.admin' },
            { type: 'link', to: '/system/roles', label: 'Roles and permissions', icon: Shield, featureFlag: 'administration.admin' },
            { type: 'link', to: '/system/ip-blocklist', label: 'Security', icon: ShieldAlert, featureFlag: 'system.blocklist' },
            { type: 'link', to: '/system/activity-log', label: 'Activity log', icon: Activity, featureFlag: 'system.activity_log' },
        ],
    },
]

/**
 * The `/settings` page's tabs, listed as their own items in the settings nav (§6.10). The page selects the tab from
 * `?tab=`; `value` must match its `TabsContent` values.
 */
export const settingsTabs = [
    { value: 'general', label: 'General', icon: Store },
    { value: 'localization', label: 'Localization', icon: Languages },
    { value: 'domains', label: 'Domains', icon: Globe },
    { value: 'datalayer', label: 'Data layer', icon: Braces },
] as const
